/* Share cart (simple permalink version)
 * No dependencies. Generates a native Shopify cart permalink off the
 * current origin (so it also works on .myshopify.com / theme-preview
 * links, not just the production domain):
 *   https://<current origin>/cart/VARIANT:QTY,VARIANT:QTY?storefront=true
 *
 * Loaded site-wide (the cart drawer exists on every page). Uses a single
 * delegated click listener instead of binding to buttons directly, because
 * the cart drawer's [data-products] region is fully replaced (innerHTML)
 * on every quantity change (see theme.CartForm.cartMarkup in theme.js),
 * which would otherwise destroy direct event bindings. Markup can appear
 * more than once per page (cart page + cart drawer):
 *
 * <div data-share-cart>
 *   <button type="button" data-share-cart-share>Share this cart</button>
 *   <span data-share-cart-status hidden></span>
 * </div>
 *
 * The single button opens the OS share sheet via the Web Share API
 * (navigator.share) where supported — Messages, WhatsApp, Mail, Copy
 * Link, etc. are then whatever the OS offers, not something this file
 * builds. Falls back to copying the link to the clipboard on browsers
 * without Web Share support (most desktop browsers).
 *
 * The customized-variants marker travels as a URL *fragment*
 * (#__shared_customized_variants=...), not a query param. Shopify's cart
 * permalink route (/cart/VARIANT:QTY,...) only applies the variant:qty
 * pairs from the path — it silently drops attributes[key]=value query
 * params, so the recipient's cart never actually got the attribute. A
 * fragment is never sent to the server (so there's nothing for Shopify to
 * drop) and browsers preserve it across the permalink's redirect to /cart.
 *
 * The value is kept in sessionStorage rather than as a cart attribute:
 * another app on this store calls /cart/update.js on its own shortly
 * after load (a free-gift app, confirmed via devtools), and Shopify's
 * /cart/update.js replaces the whole attributes hash rather than merging
 * into it, so a cart-attribute-based approach was getting silently wiped.
 * shared-cart-note.liquid always renders its markup (hidden by default);
 * applyNoteVisibility() below just toggles it client-side.
 */
(function () {
  var FEE_PRODUCT_ID = 40864066535524; // Zepto "Item Personalization" fee product
  var STORE_URL = window.location.origin;
  // Key used both as the URL fragment name and the sessionStorage key for
  // flagging customized products on the recipient's cart page.
  var NOTE_ATTR = '__shared_customized_variants';
  // Zepto Product Personalizer doesn't use one consistent property key —
  // confirmed via live cart data to vary by product/config (seen:
  // _pplr_customization_id, _pplr_attribute_data, _pplr_cart_variant,
  // _pplr_line_ref, _pplr_pricing_ref). "pplr" is the one constant across
  // all of them (and across this theme's other Zepto integration code), so
  // match on that substring instead of a single exact key. Not every
  // customized line carries a paid fee line, so this is checked
  // independently of FEE_PRODUCT_ID.
  var ZEPTO_PROPERTY_MARKER = 'pplr';

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function isZeptoCustomized(item) {
    if (!item.properties) return false;
    return Object.keys(item.properties).some(function (key) {
      return key.indexOf(ZEPTO_PROPERTY_MARKER) !== -1;
    });
  }

  function buildShareUrl(cart) {
    var qtyByVariant = {};   // merge duplicate lines (customized + plain
                             // versions of the same variant become one)
    var order = [];
    var customizedVariants = [];

    cart.items.forEach(function (item) {
      if (item.product_id === FEE_PRODUCT_ID) return; // drop fee lines
      if (isZeptoCustomized(item) && customizedVariants.indexOf(item.variant_id) === -1) {
        customizedVariants.push(item.variant_id);
      }
      if (!(item.variant_id in qtyByVariant)) {
        qtyByVariant[item.variant_id] = 0;
        order.push(item.variant_id);
      }
      qtyByVariant[item.variant_id] += item.quantity;
    });

    if (!order.length) return null;

    var pairs = order.map(function (v) { return v + ':' + qtyByVariant[v]; });
    var url = STORE_URL + '/cart/' + pairs.join(',') + '?storefront=true';
    if (customizedVariants.length) {
      url += '#' + NOTE_ATTR + '=' + encodeURIComponent(customizedVariants.join(','));
    }
    return url;
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    return Promise.resolve();
  }

  function flash(wrapper, msg) {
    var el = wrapper && wrapper.querySelector('[data-share-cart-status]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.hidden = true; }, 4000);
  }

  function handleShare(wrapper) {
    getCart().then(function (cart) {
      var url = buildShareUrl(cart);
      if (!url) { flash(wrapper, 'Your cart is empty.'); return; }

      if (navigator.share) {
        // No `text` field: some OS share sheets' "Copy" action concatenates
        // text + url into one clipboard string, which isn't a usable link.
        navigator.share({
          title: 'A cart from Modern Nursery',
          url: url
        }).catch(function (err) {
          if (err && err.name === 'AbortError') return; // user dismissed the share sheet
          copyToClipboard(url).then(function () { flash(wrapper, 'Cart link copied!'); });
        });
        return;
      }

      copyToClipboard(url).then(function () { flash(wrapper, 'Cart link copied!'); });
    }).catch(function () { flash(wrapper, 'Could not read your cart. Please try again.'); });
  }

  document.addEventListener('click', function (e) {
    var shareBtn = e.target.closest && e.target.closest('[data-share-cart-share]');
    if (shareBtn) {
      handleShare(shareBtn.closest('[data-share-cart]'));
    }
  });

  function getSharedVariantIds() {
    var raw;
    try { raw = sessionStorage.getItem(NOTE_ATTR); } catch (e) { raw = null; }
    return raw ? raw.split(',') : [];
  }

  // Un-hides the shared-cart-note.liquid markup for whichever variants are
  // in sessionStorage. Safe to call repeatedly / when nothing matches.
  function applyNoteVisibility() {
    var variantIds = getSharedVariantIds();
    if (!variantIds.length) return;

    var shown = false;
    document.querySelectorAll('[data-shared-cart-note]').forEach(function (el) {
      if (variantIds.indexOf(el.getAttribute('data-variant-id')) !== -1) {
        el.hidden = false;
        shown = true;
      }
    });

    if (shown) {
      document.querySelectorAll('[data-shared-cart-banner]').forEach(function (el) {
        el.hidden = false;
      });
    }
  }

  // Recipient side: pick up #__shared_customized_variants=... from a shared
  // link, remember it for the rest of the visit, and clean the URL.
  function captureSharedNoteFromHash() {
    var prefix = '#' + NOTE_ATTR + '=';
    if (location.hash.indexOf(prefix) !== 0) return;

    var value = decodeURIComponent(location.hash.slice(prefix.length));
    if (!value) return;

    try { sessionStorage.setItem(NOTE_ATTR, value); } catch (e) {}
    history.replaceState(null, '', location.pathname + location.search);
  }

  captureSharedNoteFromHash();
  applyNoteVisibility();

  // The cart drawer fully replaces [data-products]'s contents on every
  // add-to-cart / quantity change (see theme.CartForm.cartMarkup in
  // theme.js), so newly-injected item rows need their notes re-applied.
  var cartDrawerProducts = document.querySelector('[data-products]');
  if (cartDrawerProducts && window.MutationObserver) {
    new MutationObserver(applyNoteVisibility).observe(cartDrawerProducts, { childList: true });
  }
})();
